export const generateUuid = function (): string {
    function s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }
    return s4() + s4() + "-" + s4() + "-" + s4() + "-" + s4() + "-" + s4() + s4() + s4();
};

export function hashString(message: string) {
    let hash = 0;
    if (message.length === 0) {
        return hash;
    }
    for (let i = 0; i < message.length; i++) {
        const ch = message.charCodeAt(i);
        hash = (hash << 5) - hash + ch;
        hash &= hash;
    }
    return hash;
}

export function isEmpty(obj: any) {
    return [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;
}

export function isNumber(value: any) {
    return Number.isFinite(value);
}

//FIXME: (MA/DaBi) hack to convert bundle ID to variant ID
export function getVariantIdFromBundleId(bettyBundleId: string) {
    return bettyBundleId?.substring(0, bettyBundleId.length - 4) || "";
}

export function getBettyBundleNumberFromBettyBundleId(bettyBundleId: string) {
    return bettyBundleId?.substring(bettyBundleId.length - 4) || "";
}

export function cloneDeep<T>(value: T, cache = new WeakMap()): T {
    // Handle null or undefined
    if (value === null || value === undefined) {
        return value;
    }

    // Handle primitives (string, number, boolean, symbol, bigint)
    if (typeof value !== 'object' && typeof value !== 'function') {
        return value;
    }

    // Handle Date objects
    if (value instanceof Date) {
        return new Date(value.getTime()) as unknown as T;
    }

    // Handle RegExp objects
    if (value instanceof RegExp) {
        return new RegExp(value.source, value.flags) as unknown as T;
    }

    // Handle Arrays
    if (Array.isArray(value)) {
        return value.map(item => cloneDeep(item, cache)) as unknown as T;
    }

    // Handle Map objects
    if (value instanceof Map) {
        const result = new Map();
        cache.set(value, result);

        value.forEach((val, key) => {
            result.set(
                cloneDeep(key, cache),
                cloneDeep(val, cache)
            );
        });

        return result as unknown as T;
    }

    // Handle Set objects
    if (value instanceof Set) {
        const result = new Set();
        cache.set(value, result);

        value.forEach(val => {
            result.add(cloneDeep(val, cache));
        });

        return result as unknown as T;
    }

    // Objects and class instances
    const objectValue = value as object;

    // Check for circular references
    if (cache.has(objectValue)) {
        return cache.get(objectValue) as T;
    }

    // Create a new instance with the same prototype
    const clone = Object.create(
        Object.getPrototypeOf(objectValue)
    ) as object;

    // Store in cache before recursively cloning properties to handle circular refs
    cache.set(objectValue, clone);

    // Clone all properties including inherited ones if they're own properties of the object
    const descriptors = Object.getOwnPropertyDescriptors(objectValue);

    Object.keys(descriptors).forEach(key => {
        const descriptor = descriptors[key];

        // Clone property values if they have a value
        if (descriptor.value !== undefined) {
            descriptor.value = cloneDeep(descriptor.value, cache);
        }

        // Define the property on the clone
        Object.defineProperty(clone, key, descriptor);
    });

    return clone as T;
}

let uniqueIdCounter = 0;
export function uniqueId(prefix: string = ''): string {
    return `${prefix}${++uniqueIdCounter}`;
}
export function isNil(value: any) {
    return value === null || value === undefined;
}

export function deepEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) {
        return true;
    }

    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
        return false;
    }

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
            return false;
        }
    }

    return true;
}

export function words(str: any) {
    str = String(str);

    const pattern = /\p{L}+(?:'\p{L}+)*/gu;
    return str.match(pattern) || [];
}

export function isObject(value: any) {
    const type = typeof value;
    return value !== null && (type === 'object' || type === 'function');
}

type DebounceOptions = {
    leading?: boolean;
    maxWait?: number;
    trailing?: boolean;
};

export type DebouncedFunction<F extends (...args: any[]) => any> = {
    (...args: Parameters<F>): ReturnType<F> | undefined;
    cancel: () => void;
    flush: () => ReturnType<F> | undefined;
};

export function debounce<F extends (...args: any[]) => any>(
  func: F,
  wait: number,
  options?: DebounceOptions
): DebouncedFunction<F> {
    const FUNC_ERROR_TEXT = 'Expected a function';

    const nativeMax = Math.max;
    const nativeMin = Math.min;

    let lastArgs: Parameters<F> | undefined;
    let lastThis: ThisParameterType<F> | undefined;
    let maxWait: number | undefined;
    let result: ReturnType<F> | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    let lastCallTime: number | undefined;
    let lastInvokeTime = 0;
    let leading = false;
    let maxing = false;
    let trailing = true;

    if (typeof func !== 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
    }

    wait = Number(wait) || 0;

    if (options && typeof options === 'object') {
        leading = !!options.leading;
        maxing = 'maxWait' in options;
        maxWait = maxing ? nativeMax(Number(options.maxWait) || 0, wait) : undefined;
        trailing = 'trailing' in options ? !!options.trailing : trailing;
    }

    function invokeFunc(time: number): ReturnType<F> {
        const args = lastArgs!;
        const thisArg = lastThis!;

        lastArgs = lastThis = undefined;
        lastInvokeTime = time;
        result = func.apply(thisArg, args);
        return result as ReturnType<F>;
    }

    function leadingEdge(time: number): ReturnType<F> | undefined {
        lastInvokeTime = time;
        timerId = setTimeout(timerExpired, wait);
        return leading ? invokeFunc(time) : result;
    }

    function remainingWait(time: number): number {
        const timeSinceLastCall = time - (lastCallTime || 0);
        const timeSinceLastInvoke = time - lastInvokeTime;
        const timeWaiting = wait - timeSinceLastCall;

        return maxing
          ? nativeMin(timeWaiting, (maxWait || 0) - timeSinceLastInvoke)
          : timeWaiting;
    }

    function shouldInvoke(time: number): boolean {
        const timeSinceLastCall = time - (lastCallTime || 0);
        const timeSinceLastInvoke = time - lastInvokeTime;

        return (
          lastCallTime === undefined ||
          timeSinceLastCall >= wait ||
          timeSinceLastCall < 0 ||
          (maxing && timeSinceLastInvoke >= (maxWait || 0))
        );
    }

    function timerExpired(): void {
        const time = Date.now();
        if (shouldInvoke(time)) {
            trailingEdge(time);
        } else {
            timerId = setTimeout(timerExpired, remainingWait(time));
        }
    }

    function trailingEdge(time: number): ReturnType<F> | undefined {
        timerId = undefined;

        if (trailing && lastArgs) {
            return invokeFunc(time);
        }
        lastArgs = lastThis = undefined;
        return result;
    }

    function cancel(): void {
        if (timerId !== undefined) {
            clearTimeout(timerId);
        }
        lastInvokeTime = 0;
        lastArgs = lastCallTime = lastThis = timerId = undefined;
    }

    function flush(): ReturnType<F> | undefined {
        return timerId === undefined ? result : trailingEdge(Date.now());
    }

    function debounced(this: ThisParameterType<F>, ...args: Parameters<F>): ReturnType<F> | undefined {
        const time = Date.now();
        const isInvoking = shouldInvoke(time);

        lastArgs = args;
        lastThis = this;
        lastCallTime = time;

        if (isInvoking) {
            if (timerId === undefined) {
                return leadingEdge(lastCallTime);
            }
            if (maxing) {
                clearTimeout(timerId);
                timerId = setTimeout(timerExpired, wait);
                return invokeFunc(lastCallTime);
            }
        }
        if (timerId === undefined) {
            timerId = setTimeout(timerExpired, wait);
        }
        return result;
    }

    debounced.cancel = cancel;
    debounced.flush = flush;
    return debounced;
}